<template>
  <div class="space-y-6">
    <!-- 页面标题 -->
    <div>
      <h2 class="text-2xl font-semibold text-gray-700">注单管理</h2>
      <div class="flex items-center mt-2">
        <router-link to="/admin/dashboard" class="text-gray-600 hover:text-gray-800">首页</router-link>
        <span class="mx-2 text-gray-400">•</span>
        <span class="text-gray-600">注单列表</span>
      </div>
    </div>

    <!-- 筛选器 -->
    <SearchFilters 
      :filter-config="filterConfig"
      :initial-filters="filters"
      @search="search"
      @reset="resetFilters"
      @update:filters="updateFilters"
      ref="searchFiltersRef"
    />

    <!-- 注单表格 -->
    <div class="bg-white shadow rounded-md overflow-hidden">
      <div class="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center">
        <h3 class="text-lg leading-6 font-medium text-gray-900">注单列表</h3>
        <div>
          <button @click="exportBets" type="button" class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-500 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
            <i class="fas fa-download mr-2"></i> 导出
          </button>
        </div>
      </div>
      
      <TableList
        :columns="columns"
        :items="bets"
        :loading="loading"
        :total-items="totalItems"
        :current-page="currentPage"
        :items-per-page="itemsPerPage"
        :initial-sort-key="sortBy"
        :initial-sort-order="sortDirection"
        empty-text="暂无投注记录数据"
        @page-change="handlePageChange"
        @sort="handleSort"
      >
        <!-- 自定义单元格渲染 -->
        <template #cell-fundsn="{ item }">
          <div class="flex items-center space-x-1">
            <span class="font-medium">{{ item.fundsn }}</span>
            <span v-if="item.is_abnormal" class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
              异常
            </span>
          </div>
        </template>

        <template #cell-vendor_username="{ item }">
          <a 
            href="#" 
            @click.prevent="viewPlayerDetails(item.user_id)"
            class="text-indigo-600 hover:text-indigo-900"
          >
            {{ item.vendor_username }}
          </a>
        </template>

        <template #cell-bet_amount="{ item }">
          <span class="font-medium text-blue-600">{{ formatAmount(item.bet_amount, item.currency) }}</span>
        </template>

        <template #cell-payout="{ item }">
          <span :class="parseFloat(item.payout) > 0 ? 'font-medium text-green-600' : parseFloat(item.payout) < 0 ? 'font-medium text-red-600' : 'text-gray-500'">
            {{ formatPayoutAmount(item.payout, item.currency) }}
          </span>
        </template>

        <template #cell-game_info="{ item }">
          <div class="flex flex-col">
            <span class="text-gray-800 font-medium">{{ item.system || '-' }}</span>
            <span class="text-xs text-gray-500">{{ item.game || '-' }}</span>
          </div>
        </template>

        <template #cell-game_category="{ item }">
          <span :class="getBadgeClass(item.game_category)" class="px-2 py-1 text-xs rounded-full inline-flex items-center">
            {{ getGameCategoryName(item) }}
          </span>
        </template>

        <template #cell-status="{ item }">
          <div class="flex items-center space-x-1">
            <span 
              :class="[
                'px-2 inline-flex text-xs leading-5 font-semibold rounded-full',
                item.status === 1 ? 'bg-green-100 text-green-800' : 
                item.status === 0 ? 'bg-yellow-100 text-yellow-800' :
                item.status === 2 ? 'bg-gray-100 text-gray-800' :
                'bg-blue-100 text-blue-800'
              ]"
            >
              {{ item.status_text || getStatusText(item.status) }}
            </span>
            <span v-if="item.is_abnormal" class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
              异常
            </span>
          </div>
        </template>

        <template #cell-bet_time="{ item }">
          {{ item.bet_time || formatDatetime(item.bet_at) }}
        </template>

        <template #cell-actions="{ item }">
          <div class="flex items-center space-x-2">
            <button
              @click="viewBetDetail(item.id, item.fundsn, item.system)"
              class="text-blue-600 hover:text-blue-900"
              title="查看详情"
            >
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
              </svg>
            </button>
            <button
              @click="viewGameRecords(item.id, item.fundsn, item.system)"
              class="text-indigo-600 hover:text-indigo-900"
              title="游戏记录"
            >
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"></path>
              </svg>
            </button>
            <button
              @click="markAbnormal(item.id, item.fundsn, item.system)"
              class="text-amber-600 hover:text-amber-800"
              title="标记异常"
            >
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
              </svg>
            </button>
          </div>
        </template>
      </TableList>
    </div>

    <!-- 模态框引用 -->
    <BetDetailsModal
      :visible="showBetDetailModal"
      :bet-data="currentBetData"
      @close="closeBetDetailModal"
      @mark-resolved="handleMarkResolved"
      @bet-updated="handleBetUpdated"
    />

    <GameRecordsModal
      :visible="showGameRecordsModal"
      :bet-id="currentFundsn"
      :system="currentSystem"
      @close="closeGameRecordsModal"
    />

    <AbnormalBetModal
      :visible="showAbnormalModal"
      :bet-id="currentFundsn"
      :system="currentSystem"
      @close="closeAbnormalModal"
      @submit-success="handleAbnormalSubmitted"
    />
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import AdminApiClient from '@/utils/admin-api-client';
import SearchFilters from '@/components/common/SearchFilters.vue';
import TableList from '@/components/common/TableList.vue';
import BetDetailsModal from '@/components/bets/BetDetailsModal.vue';
import GameRecordsModal from '@/components/bets/GameRecordsModal.vue';
import AbnormalBetModal from '@/components/bets/AbnormalBetModal.vue';
import { Teleport } from 'vue';

const router = useRouter();
const apiClient = new AdminApiClient();

// 状态变量
const loading = ref(false);
const bets = ref([]);
const totalItems = ref(0);
const currentPage = ref(1);
const totalPages = ref(1);
const itemsPerPage = ref(15);
const sortBy = ref('');
const sortDirection = ref('desc');

// 获取今天的日期范围
const today = new Date();
const todayStart = today.toISOString().split('T')[0] + ' 00:00:00';
const todayEnd = today.toISOString().split('T')[0] + ' 23:59:59';

// 筛选器状态
const filters = reactive({
  bet_id: '',
  username: '',
  user_id: '',
  status: '',
  start_time: todayStart,
  end_time: todayEnd,
  timezone: 'UTC+8',
  system: ''
});

// 搜索过滤组件引用
const searchFiltersRef = ref(null);

// 选项数据
const statusOptions = ref([
  { value: '0', label: '未结算' },
  { value: '1', label: '已结算' },
  { value: '2', label: '已取消' }
]);

const vendorOptions = ref([]);

// 注单详情模态框状态
const showBetDetailModal = ref(false);

// 游戏记录模态框状态
const showGameRecordsModal = ref(false);
const gameRecords = ref([]);
const gameRecordsLoading = ref(false);
const gameRecordsError = ref(false);
const gameBetSummary = ref({});

// 游戏记录详情状态
const showGameRecordDetail = ref(false);
const gameRecordDetailLoading = ref(false);
const gameRecordDetailContent = ref('');

// 标记异常模态框状态
const showAbnormalModal = ref(false);
const abnormalReason = ref('');
const abnormalDescription = ref('');
const abnormalError = ref('');
const abnormalSubmitting = ref(false);

// 当前操作的注单信息
const currentBetId = ref('');
const currentFundsn = ref('');
const currentSystem = ref('');
const currentBetData = ref({});

// 表格列配置
const columns = [
  { key: 'fundsn', label: '注单ID', sortable: true },
  { key: 'vendor_username', label: '用户名', sortable: true },
  { key: 'bet_amount', label: '投注金额', sortable: true },
  { key: 'payout', label: '派彩金额', sortable: true },
  { key: 'game_info', label: '游戏平台/游戏', sortable: false },
  { key: 'game_category', label: '游戏类别', sortable: true },
  { key: 'status', label: '状态', sortable: true },
  { key: 'bet_time', label: '投注时间', sortable: true },
  { key: 'actions', label: '操作', sortable: false }
];

// 状态标签映射
const statusLabels = {
  0: '未结算',
  1: '已结算',
  2: '已取消'
};

// 过滤器配置
const filterConfig = ref([
  {
    id: 'bet_id',
    type: 'input',
    label: '注单ID',
    placeholder: '输入注单ID',
    icon: 'fas fa-search'
  },
  {
    id: 'username',
    type: 'input',
    label: '用户名',
    placeholder: '输入用户名',
    icon: 'fas fa-user'
  },
  {
    id: 'user_id',
    type: 'input',
    label: '用户ID',
    placeholder: '输入用户ID',
    icon: 'fas fa-id-card'
  },
  {
    id: 'status',
    type: 'select',
    label: '状态',
    placeholder: '全部状态',
    options: statusOptions,
    icon: 'fas fa-check-circle'
  },
  {
    id: 'date_range',
    type: 'dateRange',
    label: '日期范围',
    placeholder: '选择日期范围',
    icon: 'fas fa-calendar',
    startField: 'start_time',
    endField: 'end_time'
  },
  {
    id: 'timezone',
    type: 'select',
    label: '时区',
    icon: 'fas fa-clock',
    options: [
      { value: 'UTC+8', label: 'UTC+8 (东八区/北京时间)' },
      { value: 'UTC+0', label: 'UTC+0 (格林威治标准时间)' },
      { value: 'UTC-5', label: 'UTC-5 (美国东部时间)' },
      { value: 'UTC-8', label: 'UTC-8 (美国西部时间)' },
      { value: 'UTC+1', label: 'UTC+1 (中欧时间)' },
      { value: 'UTC+9', label: 'UTC+9 (日本时间)' }
    ],
    excludeFromReset: true
  },
  {
    id: 'system',
    type: 'select',
    label: '游戏平台',
    placeholder: '全部平台',
    options: vendorOptions, // 将在loadVendorOptions中动态填充
    icon: 'fas fa-building'
  }
]);

// 生命周期钩子
onMounted(() => {
  loadVendorOptions();
  loadBetsData();
});

// 加载供应商选项
async function loadVendorOptions() {
  try {
    const response = await apiClient.getCompareOptions();
    if (response && response.success && response.data && response.data.vendors) {
      // 将对象格式转换为数组格式
      const vendorsObj = response.data.vendors;
      vendorOptions.value = Object.keys(vendorsObj).map(key => ({
        value: key,
        label: vendorsObj[key]
      }));
    }
  } catch (error) {
    console.error('Failed to load vendor options:', error);
  }
}

// 加载注单数据
async function loadBetsData(page) {
  if (loading.value) return;
  
  loading.value = true;
  
  // 如果提供了页码，更新当前页
  if (page) {
    currentPage.value = Number(page);
  }
  
  try {
    const params = {
      page: currentPage.value,
      per_page: itemsPerPage.value,
      ...getApiFilters()
    };
    
    const response = await apiClient.getBets(params);
    if (response && response.success) {
      const data = response.data || {};
      
      bets.value = data.list || [];
      totalItems.value = Number(data.total) || 0;
      totalPages.value = Number(data.last_page) || 1;
      currentPage.value = Number(data.current_page) || 1;
    } else {
      bets.value = [];
      console.error('Failed to load bets:', response?.message || 'Unknown error');
    }
  } catch (error) {
    console.error('Failed to load bets:', error);
    bets.value = [];
  } finally {
    loading.value = false;
  }
}

// 获取API过滤参数
function getApiFilters() {
  const apiFilters = {};
  
  // 复制非空筛选条件
  Object.keys(filters).forEach(key => {
    if (filters[key] !== null && filters[key] !== undefined && filters[key] !== '') {
      apiFilters[key] = filters[key];
    }
  });
  
  return apiFilters;
}

// 搜索按钮处理程序
function search(searchFilters) {
  console.log('搜索条件:', searchFilters);
  // 更新所有过滤器
  Object.keys(searchFilters).forEach(key => {
    filters[key] = searchFilters[key];
  });
  
  // 重置页码到第一页
  currentPage.value = 1;
  
  // 加载数据
  loadBetsData(1);
}

// 重置筛选条件
function resetFilters(resetFilters) {
  console.log('重置条件:', resetFilters);
  
  // 使用重置后的过滤器
  Object.keys(resetFilters).forEach(key => {
    filters[key] = resetFilters[key];
  });
  
  // 确保日期字段被正确重置为空字符串
  filters.start_time = '';
  filters.end_time = '';
  
  // 清除本地缓存
  clearLocalStorageCache();
  
  // 重置页码到第一页
  currentPage.value = 1;
  
  // 加载数据
  loadBetsData(1);
}

// 清除localStorage缓存
function clearLocalStorageCache() {
  // 清除玩家筛选相关的所有缓存
  localStorage.removeItem('filter_player_username');
  localStorage.removeItem('filter_player_id');
  
  // 可以根据需要清除其他相关缓存
  console.log('已清除玩家筛选缓存');
}

// 格式化金额显示
function formatAmount(amount, currency) {
  if (amount === null || amount === undefined) return '-';
  
  const numAmount = parseFloat(amount);
  const formatter = new Intl.NumberFormat('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  
  return `${formatter.format(numAmount)} ${currency || ''}`.trim();
}

// 格式化派彩金额，带正负号
function formatPayoutAmount(amount, currency) {
  if (amount === null || amount === undefined) return '-';
  
  const numAmount = parseFloat(amount);
  const formatter = new Intl.NumberFormat('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  
  let prefix = numAmount > 0 ? '+' : '';
  
  return `${prefix}${formatter.format(numAmount)} ${currency || ''}`.trim();
}

// 格式化日期时间显示
function formatDatetime(datetime) {
  if (!datetime) return '-';
  
  const date = new Date(datetime);
  if (isNaN(date.getTime())) return datetime;
  
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).replace(/\//g, '-');
}

// 获取状态文本
function getStatusText(status) {
  const statusMap = {
    '0': '未结算',
    '1': '已结算',
    '2': '已取消'
  };
  
  return statusMap[status] || status;
}

// 获取状态徽章类名
function getStatusBadgeClass(status) {
  const classMap = {
    '0': 'px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800',
    '1': 'px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800',
    '2': 'px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800'
  };
  
  return classMap[status] || 'px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800';
}

// 导出注单
function exportBets() {
  const exportUrl = apiClient.getBetsExportUrl(getApiFilters());
  window.open(exportUrl, '_blank');
}

// 查看注单详情
async function viewBetDetail(id, fundsn, system) {
  // 设置当前操作的注单信息
  currentBetId.value = id;
  currentFundsn.value = fundsn;
  currentSystem.value = system;
  
  // 从当前列表中查找对应的注单数据
  const betData = bets.value.find(bet => bet.id === id || bet.fundsn === fundsn);
  if (betData) {
    currentBetData.value = betData;
  } else {
    // 如果找不到对应数据，设置一个基本信息
    currentBetData.value = {
      id: id,
      fundsn: fundsn,
      system: system
    };
  }
  
  // 显示详情模态框
  showBetDetailModal.value = true;
}

// 关闭注单详情模态框
function closeBetDetailModal() {
  showBetDetailModal.value = false;
}

// 查看游戏记录
async function viewGameRecords(id, fundsn, system) {
  gameRecordsLoading.value = true;
  gameRecords.value = [];
  gameRecordsError.value = false;
  gameBetSummary.value = {};
  currentBetId.value = id;
  currentFundsn.value = fundsn;
  currentSystem.value = system;
  showGameRecordsModal.value = true;
  
  try {
    // 测试模拟数据，以确保弹框显示
    const mockData = {
      status: true,
      data: {
        bet: {
          fundsn: fundsn || '123456789',
          roundid: 'R-' + (Math.floor(Math.random() * 1000000)),
          bet_amount: '100.00',
          payout: Math.random() > 0.5 ? '120.00' : '-100.00',
          currency: 'CNY',
          user_id: '10001',
          vendor_username: 'testuser',
          system: system || 'pg'
        },
        records: [
          {
            id: '1001',
            fundsn: fundsn || '123456789',
            roundid: 'R-' + (Math.floor(Math.random() * 1000000)),
            username: 'testuser',
            action_type: 'bet',
            action_type_name: '投注',
            amount: '100.00',
            game_code: 'game001',
            currency: 'CNY',
            created_at: new Date().toISOString()
          },
          {
            id: '1002',
            fundsn: fundsn || '123456789',
            roundid: 'R-' + (Math.floor(Math.random() * 1000000)),
            username: 'testuser',
            action_type: 'win',
            action_type_name: '派彩',
            amount: '120.00',
            game_code: 'game001',
            currency: 'CNY',
            created_at: new Date().toISOString()
          }
        ]
      }
    };
    
    // 使用模拟数据进行测试，在实际环境中应该使用API调用
    /*
    const response = await apiClient.getGameRecords(fundsn, system);
    */
    const response = mockData;
    
    if (response && response.status) {
      const { records, bet } = response.data;
      
      if (bet) {
        gameBetSummary.value = bet;
      }
      
      if (records && records.length > 0) {
        gameRecords.value = records;
      } else {
        gameRecordsError.value = true;
      }
    } else {
      gameRecordsError.value = true;
    }
  } catch (error) {
    console.error('Failed to load game records:', error);
    gameRecordsError.value = true;
  } finally {
    gameRecordsLoading.value = false;
  }
}

// 关闭游戏记录模态框
function closeGameRecordsModal() {
  showGameRecordsModal.value = false;
}

// 标记注单为异常
function markAbnormal(id, fundsn, system) {
  currentBetId.value = id;
  currentFundsn.value = fundsn;
  currentSystem.value = system;
  abnormalReason.value = '';
  abnormalDescription.value = '';
  abnormalError.value = '';
  showAbnormalModal.value = true;
}

// 关闭标记异常模态框
function closeAbnormalModal() {
  if (abnormalSubmitting.value) return;
  showAbnormalModal.value = false;
}

// 更新过滤器
function updateFilters(newFilters) {
  console.log('更新过滤器:', newFilters);
  Object.keys(newFilters).forEach(key => {
    filters[key] = newFilters[key];
  });
}

// 处理页码变化
function handlePageChange(page) {
  if (page < 1 || page > totalPages.value || loading.value) {
    return;
  }
  
  loadBetsData(page);
}

// 处理排序变化
function handleSort({ key, direction }) {
  sortBy.value = key;
  sortDirection.value = direction;
  loadBetsData(1);
}

// 查看玩家详情
function viewPlayerDetails(userId) {
  if (!userId) return;
  
  // 首先检查用户名
  const bet = bets.value.find(bet => bet.user_id === userId);
  if (bet && bet.vendor_username) {
    console.log(`导航到玩家管理页面并筛选用户: ${bet.vendor_username} (ID: ${userId})`);
    
    // 将用户名保存到localStorage, 以便PlayersList可以读取并应用筛选
    localStorage.setItem('filter_player_username', bet.vendor_username);
    
    // 导航到玩家列表页面
    router.push({
      path: '/admin/players',
      query: { 
        username: bet.vendor_username
      }
    });
  } else {
    // 没有用户名信息，只使用ID导航
    console.log(`导航到玩家管理页面 (ID: ${userId})`);
    
    // 清除旧的筛选条件
    localStorage.removeItem('filter_player_username');
    localStorage.setItem('filter_player_id', userId);
    
    // 导航到玩家列表页面
    router.push({
      path: '/admin/players',
      query: { user_id: userId }
    });
  }
}

// 获取游戏类别徽章类名
function getBadgeClass(category) {
  const classMap = {
    'sport': 'bg-green-100 text-green-800',
    'casino': 'bg-blue-100 text-blue-800',
    'slot': 'bg-purple-100 text-purple-800',
    'lottery': 'bg-yellow-100 text-yellow-800',
    'fish': 'bg-indigo-100 text-indigo-800',
    'chess': 'bg-red-100 text-red-800',
    'arcade': 'bg-pink-100 text-pink-800',
    'table': 'bg-teal-100 text-teal-800'
  };
  
  return classMap[category] || 'bg-gray-100 text-gray-800';
}

// 获取游戏类别名称
function getGameCategoryName(item) {
  const categoryMap = {
    'sport': '体育',
    'casino': '真人',
    'slot': '老虎机',
    'lottery': '彩票',
    'fish': '捕鱼',
    'chess': '棋牌',
    'arcade': '街机',
    'table': '桌牌'
  };
  
  return categoryMap[item.game_category] || item.game_category || '-';
}

// 处理异常标记提交成功
function handleAbnormalSubmitted(data) {
  console.log('异常标记已提交:', data);
  
  // 立即更新表格中对应的注单项，不需要重新加载整个表格
  const betIndex = bets.value.findIndex(bet => bet.fundsn === data.id || bet.id === data.id);
  if (betIndex > -1) {
    // 更新异常标记信息
    bets.value[betIndex].is_abnormal = true;
    bets.value[betIndex].abnormal_id = data.abnormal_id;
    bets.value[betIndex].abnormal_reason = data.reason;
    bets.value[betIndex].abnormal_description = data.description;
    bets.value[betIndex].abnormal_reason_text = getAbnormalReasonText(data.reason);
  } else {
    // 如果找不到对应的注单，则重新加载当前页数据
    loadBetsData(currentPage.value);
  }
}

// 获取异常原因文本
function getAbnormalReasonText(reason) {
  const reasonMap = {
    'system_error': '系统错误',
    'amount_error': '金额错误',
    'betting_anomaly': '投注异常',
    'other_issue': '其他问题'
  };
  
  return reasonMap[reason] || reason;
}

// 处理标记为已解决
async function handleMarkResolved(abnormalId) {
  if (!abnormalId) {
    console.error('标记为已解决失败：缺少异常ID');
    throw new Error('缺少异常ID');
  }
  // 立即更新表格中对应的注单项，不需要重新加载整个表格
  const betIndex = bets.value.findIndex(bet => bet.abnormal_id === abnormalId);
  if (betIndex > -1) {
    // 更新异常标记信息
    bets.value[betIndex].is_abnormal = false;
  } else {
    // 如果找不到对应的注单，则重新加载当前页数据
    loadBetsData(currentPage.value);
  }
}
// 处理注单更新
async function handleBetUpdated(data) {
  console.log('注单信息已更新:', data);
  
  // 重新加载注单数据以确保显示最新状态
  await loadBetsData(currentPage.value);
}
</script>

<style scoped>
.loading-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #e5e7eb;
  border-radius: 50%;
  border-top: 3px solid #1aaa55;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 模态框样式 */
.fixed {
  position: fixed !important;
  z-index: 9999 !important;
}

.max-h-\[90vh\] {
  max-height: 90vh !important;
}

.max-h-\[70vh\] {
  max-height: 70vh !important;
}

/* Flatpickr customization */
:deep(.flatpickr-calendar) {
  background: #fff;
  border-radius: 0.375rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

:deep(.flatpickr-day.selected),
:deep(.flatpickr-day.startRange),
:deep(.flatpickr-day.endRange) {
  background: #3b82f6;
  border-color: #3b82f6;
}

:deep(.flatpickr-day.inRange) {
  background: #dbeafe;
  border-color: #dbeafe;
  box-shadow: -5px 0 0 #dbeafe, 5px 0 0 #dbeafe;
}

/* 确保模态框层级足够高 */
:deep(.modal-overlay) {
  z-index: 9999 !important;
  position: fixed !important;
  inset: 0 !important;
}

:deep(.modal-container) {
  max-width: 90vw;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

/* 添加全局过渡效果 */
.modal-overlay-enter-active,
.modal-overlay-leave-active {
  transition: opacity 0.3s ease;
}

.modal-overlay-enter-from,
.modal-overlay-leave-to {
  opacity: 0;
}

.modal-container-enter-active,
.modal-container-leave-active {
  transition: all 0.3s ease;
}

.modal-container-enter-from,
.modal-container-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}
</style> 